import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { invalidateFor } from "@/lib/cache-registry";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import { useAuth } from "@/contexts/AuthContext";
import { logEvent } from "@/hooks/use-event-log";
import { useOwnsLeadList } from "@/hooks/use-lead-list-ownership";
import {
  FEATURE_FLAGS,
  FEATURE_FLAG_EVENT_TYPE,
  resolveFeatureFlagLevel,
  systemConfigKeyForFlag,
  type FeatureFlagKey,
  type FeatureFlagRolloutLevel,
} from "@/lib/feature-flags";

type FeatureFlagLevelMap = Partial<Record<FeatureFlagKey, FeatureFlagRolloutLevel>>;

const FEATURE_FLAG_KEYS = Object.keys(FEATURE_FLAGS) as FeatureFlagKey[];

/**
 * Henter gjeldende rollout-nivå for ALLE registrerte flagg i ett kall (ett
 * rundtrip mot `system_config`, ikke ett per `useFeatureFlag`-kall). Brukes
 * både av `useFeatureFlag` og av Funksjonsbrytere-panelet.
 *
 * queryKey registrert i `CACHE_REGISTRY` under `system_config`
 * (`src/lib/cache-registry/registry.ts`).
 */
export function useFeatureFlagLevels() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["feature-flags"],
    queryFn: async (): Promise<FeatureFlagLevelMap> => {
      if (FEATURE_FLAG_KEYS.length === 0) return {};
      const { data, error } = await supabase
        .from("system_config")
        .select("key, value")
        .in("key", FEATURE_FLAG_KEYS);
      if (error) throw error;
      const map: FeatureFlagLevelMap = {};
      for (const row of data ?? []) {
        if ((FEATURE_FLAG_KEYS as string[]).includes(row.key)) {
          map[row.key as FeatureFlagKey] = row.value as FeatureFlagRolloutLevel;
        }
      }
      return map;
    },
    // Vent på innlogget sesjon. Kalles hooken før sesjonen er klar (fra en
    // provider eller før `AuthGate` vet om brukeren), går spørringen som anon,
    // feiler med 401 og prøves bare én gang til. Da sto tilgangene feilet for
    // resten av økten, og `/` sendte brukeren til Hjelp.
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Resolverer ett flagg til boolean for AKTIV bruker. Bruker
 * `useImpersonation()` (ikke `useAuth()`) med hensikt: en impersonerende
 * superadmin skal se flatene slik brukeren de impersonerer ville sett dem,
 * jf. CLAUDE.md "Bruk ALLTID useImpersonation() for å hente aktiv bruker".
 * Manglende rad / manglende data tolkes som "off" (samme fail-safe som
 * edge function-lesemønsteret i `src/lib/feature-flags.ts`).
 */
export function useFeatureFlag(key: FeatureFlagKey): boolean {
  const { activeUser } = useImpersonation();
  const { data } = useFeatureFlagLevels();
  const ownsLeadList = useOwnsLeadList();
  const level = data?.[key];
  // VIB-3032: eier av en leadliste ser leads-flagget som på, slik rollen Møtebooker gjør.
  if (key === "feature_crm_leads_moter" && ownsLeadList && level && level !== "off") return true;
  return resolveFeatureFlagLevel(level, activeUser?.role, key);
}

interface UpdateFeatureFlagLevelInput {
  key: FeatureFlagKey;
  level: FeatureFlagRolloutLevel;
}

/**
 * Endrer rollout-nivået for et flagg. Kalles kun fra superadmin-gatet UI
 * (Funksjonsbrytere-panelet) — men aktøren i audit-loggen er ALLTID den
 * FAKTISK innloggede brukeren (`useAuth().user`), aldri en impersonert
 * bruker, jf. CLAUDE.md audit-regel 1 for sikkerhets-/konfig-events.
 */
export function useUpdateFeatureFlagLevel() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ key, level }: UpdateFeatureFlagLevelInput) => {
      const configKey = systemConfigKeyForFlag(key);
      const { data: before, error: readError } = await supabase
        .from("system_config")
        .select("value")
        .eq("key", configKey)
        .maybeSingle();
      if (readError) throw readError;
      const oldLevel = (before?.value as FeatureFlagRolloutLevel | undefined) ?? "off";

      const { error } = await supabase.from("system_config").upsert(
        {
          key: configKey,
          value: level,
          updated_by: user?.id ?? null,
          updated_at: new Date().toISOString(),
        },
        { onConflict: "key" },
      );
      if (error) throw error;
      return { key, oldLevel, newLevel: level };
    },
    onSuccess: async ({ key, oldLevel, newLevel }) => {
      await invalidateFor(queryClient, "system_config", { key });
      logEvent({
        event_type: FEATURE_FLAG_EVENT_TYPE,
        entity_type: "feature_flag",
        entity_id: key,
        user_id: user?.id,
        payload: { key, old_level: oldLevel, new_level: newLevel },
      });
    },
  });
}
